<template>
  <div class="form-richiesta-assistenza">
    <validation-observer v-if="!api_success" v-slot="{ invalid }">
      <b-form class="form-richiesta-assistenza__box" @submit.prevent>
        <b-row>
          <b-col md="6">
            <validation-provider v-slot="{ errors }" rules="required|alpha_spaces|min:2" name="Nome">
              <div class="form-richiesta-assistenza__field">
                <label class="form-richiesta-assistenza__label">Nome*</label>
                <vb-input v-model="first_name" :vb_options="{ autocomplete: 'given-name' }" />
                <span class="vee--errors">{{ errors[0] }}</span>
              </div>
            </validation-provider>
          </b-col>

          <b-col md="6">
            <validation-provider v-slot="{ errors }" rules="required|alpha_spaces|min:2" name="Cognome">
              <div class="form-richiesta-assistenza__field">
                <label class="form-richiesta-assistenza__label">Cognome*</label>
                <vb-input v-model="last_name" :vb_options="{ autocomplete: 'family-name' }" />
                <span class="vee--errors">{{ errors[0] }}</span>
              </div>
            </validation-provider>
          </b-col>

          <b-col md="6">
            <validation-provider v-slot="{ errors }" rules="required|email" name="Email">
              <div class="form-richiesta-assistenza__field">
                <label class="form-richiesta-assistenza__label">
                  Email*
                  <span v-b-popover.hover.top="email_tooltip" class="form-richiesta-assistenza__info">
                    <vb-icon name="info" :size="12" :color="sass('gray-600')" />
                  </span>
                </label>
                <vb-input v-model="form_email" :vb_options="{ type: 'email', autocomplete: 'email', readonly: !!email }" />
                <span class="vee--errors">{{ errors[0] }}</span>
              </div>
            </validation-provider>
          </b-col>

          <b-col md="6">
            <b-row>
              <b-col cols="5">
                <validation-provider v-slot="{ errors }" rules="required" name="Prefisso">
                  <div class="form-richiesta-assistenza__field">
                    <label class="form-richiesta-assistenza__label">Prefisso*</label>
                    <vb-select
                      v-model="phone_prefix"
                      :vb_options="{
                        options: countries,
                        text_field: 'phone_display',
                        value_field: 'phone_code',
                      }"
                    />
                    <span class="vee--errors">{{ errors[0] }}</span>
                  </div>
                </validation-provider>
              </b-col>

              <b-col cols="7">
                <validation-provider v-slot="{ errors }" rules="required|numeric|min:6" name="Telefono">
                  <div class="form-richiesta-assistenza__field">
                    <label class="form-richiesta-assistenza__label">Telefono*</label>
                    <vb-input v-model="phone" :vb_options="{ type: 'tel', autocomplete: 'tel-national' }" />
                    <span class="vee--errors">{{ errors[0] }}</span>
                  </div>
                </validation-provider>
              </b-col>
            </b-row>
          </b-col>

          <b-col md="6">
            <validation-provider v-slot="{ errors }" rules="min:9|max:32" name="Codice di assistenza">
              <div class="form-richiesta-assistenza__field">
                <label class="form-richiesta-assistenza__label">
                  Codice di assistenza
                  <span v-b-popover.hover.top="confirmcode_tooltip" class="form-richiesta-assistenza__info">
                    <vb-icon name="info" :size="12" :color="sass('gray-600')" />
                  </span>
                </label>
                <vb-input v-model="form_confirmcode" :vb_options="{ placeholder: `es. ${confirmcode_prefix}XXXXXX`, readonly: !!confirmcode }" />
                <span class="vee--errors">{{ errors[0] }}</span>
              </div>
            </validation-provider>
          </b-col>

          <b-col md="6">
            <validation-provider v-slot="{ errors }" rules="required" name="Tipologia richiesta">
              <div class="form-richiesta-assistenza__field">
                <label class="form-richiesta-assistenza__label">Tipologia richiesta*</label>
                <vb-select
                  v-model="request_type"
                  :class="{ 'form-richiesta-assistenza__select--empty': !request_type }"
                  :vb_options="{ options: request_type_options }"
                />
                <span class="vee--errors">{{ errors[0] }}</span>
              </div>
            </validation-provider>
          </b-col>

          <b-col cols="12">
            <validation-provider v-slot="{ errors }" rules="required" name="Richiesta">
              <div class="form-richiesta-assistenza__field">
                <label class="form-richiesta-assistenza__label">Richiesta*</label>
                <b-form-textarea
                  v-model="message"
                  class="form-richiesta-assistenza__textarea"
                  placeholder="Descrivi la tua richiesta nel dettaglio..."
                  maxlength="5000"
                  no-resize
                  rows="5"
                />
                <span class="vee--errors">{{ errors[0] }}</span>
              </div>
            </validation-provider>
          </b-col>
        </b-row>

        <p v-if="api_error" class="form-richiesta-assistenza__api-error">{{ api_error }}</p>

        <div class="form-richiesta-assistenza__footer">
          <vb-checkbox v-model="consent" class="form-richiesta-assistenza__consent">
            <span class="vb-check__title pl-1 d-inline-block align-middle">
              Dichiaro di aver letto e accettato le
              <router-link :to="{ name: 'terms' }" target="_blank">Condizioni d'uso</router-link>
              e la
              <a :href="url_privacy" target="_blank" rel="noopener">Privacy Policy</a>,
              incluse le modalità di trattamento dei dati.
            </span>
          </vb-checkbox>

          <vb-button
            :vb_options="{
              variant: 'info',
              text: 'Invia',
              disabled: invalid || !consent || loading,
              classes: 'form-richiesta-assistenza__submit',
            }"
            @click="submit"
          />
        </div>
      </b-form>
    </validation-observer>

    <b-alert v-else variant="info" class="text-center" show>
      <strong>{{ api_success }}</strong>
    </b-alert>
  </div>
</template>

<script>
import Vue from 'vue'
import { VueReCaptcha } from 'vue-recaptcha-v3'
import { service_handler } from '@/services'
import { countries } from '@/resources'

const FORM_TYPE = 'form-contatti'
const FORM_LABEL = 'form-richiesta-assistenza'
const GENERIC_ERROR = 'Impossibile inviare la richiesta, riprova più tardi.'

export default {
  name: 'form-richiesta-assistenza',
  props: {
    request_types: {
      type: Array,
      required: true,
    },
    confirmcode: {
      type: String,
      default: '',
    },
    email: {
      type: String,
      default: '',
    },
    confirmcode_prefix: {
      type: String,
      default: 'BLF',
    },
  },
  data() {
    return {
      countries,
      email_tooltip: 'Se hai già effettuato una prenotazione, inserisci l\'indirizzo email utilizzato per l\'acquisto.',
      first_name: '',
      last_name: '',
      form_email: this.email || '',
      phone_prefix: '39',
      phone: '',
      form_confirmcode: this.confirmcode || '',
      request_type: '',
      message: '',
      consent: false,
      loading: false,
      recaptcha_enabled: this.get_config('recaptcha.enabled', false),
      api_success: '',
      api_error: '',
    }
  },
  computed: {
    confirmcode_tooltip() {
      return `Inserisci il tuo codice assistenza che inizia per ${this.confirmcode_prefix}. Lo trovi nella pagina e nell'email di conferma.`
    },
    request_type_options() {
      return [
        { value: '', text: 'Seleziona un argomento', disabled: true },
        ...this.request_types,
      ]
    },
    url_privacy() {
      return this.get_config('site.urlPrivacy', '')
    },
    track_context() {
      return { form_type: FORM_TYPE, form_label: FORM_LABEL }
    },
  },
  mounted() {
    if (this.recaptcha_enabled) {
      Vue.use(VueReCaptcha, {
        siteKey: this.get_config('recaptcha.siteKey', ''),
      })
    }
  },
  methods: {
    async submit() {
      this.loading = true
      this.api_error = ''
      this.track_form({
        event: `${FORM_LABEL}:submit`,
        data: { type: 'info' },
        context: this.track_context,
      })

      let recaptcha_token = ''
      if (this.recaptcha_enabled) {
        try {
          await this.$recaptchaLoaded()
          recaptcha_token = await this.$recaptcha('form_submitter')
        } catch (e) {
          this.loading = false
          this.api_error = GENERIC_ERROR
          this.track_form({
            event: `${FORM_LABEL}:submit:fail`,
            data: {
              type: 'error',
              reason: 'Recaptcha',
              message: e && e.message ? e.message : undefined,
            },
            context: this.track_context,
          })
          return
        }
      }

      this.send(recaptcha_token)
    },
    send(recaptcha_token) {
      const parameters = {
        recaptcha: recaptcha_token,
        fields: {
          firstName: this.first_name,
          lastName: this.last_name,
          email: this.form_email,
          phone: `+${this.phone_prefix} ${this.phone}`,
          requestType: this.request_type,
          confirmcode: this.form_confirmcode,
          message: this.message,
        },
        settings: {
          type: FORM_TYPE,
          label: FORM_LABEL,
        },
      }

      service_handler('formsSubmitter', parameters, {
        modal: false,
        track: {
          name: FORM_LABEL,
          emit_submit: false,
          context: this.track_context,
        },
      })
        .then(
          (success) => {
            this.api_success = success.data.msg || 'Richiesta ricevuta'
            this.loading = false
          },
          (error) => {
            this.log('[form-richiesta-assistenza.vue] - error')
            this.log(error.response)

            const data = error.response && error.response.data
            this.api_error = (data && data.msg) || GENERIC_ERROR
            this.loading = false
          }
        )
    },
  },
}
</script>

<style lang="scss" scoped>
.form-richiesta-assistenza {

  &__box {
    padding: $grid-gutter-20;
    background-color: $white;
    border-radius: $border-radius-lg * 2;
  }

  &__field {
    margin-bottom: $grid-gutter-width / 2;

    .vee--errors {
      display: block;
      padding: 0;

      &:not(:empty) {
        padding-top: $grid-gutter-width / 4;
      }
    }
  }

  &__label {
    display: flex;
    align-items: center;
    margin-bottom: $grid-gutter-width / 4;
    font-size: $font-size-md;
    line-height: 1.2;
    color: $gray-900;
  }

  &__info {
    display: inline-flex;
    margin-left: $grid-gutter-width / 6;
    cursor: pointer;
  }

  ::v-deep .vb-input,
  ::v-deep .vb-select {
    height: 52px;
    padding-left: $grid-gutter-20;
    font-size: $font-size-base;
    color: $gray-900;
  }

  ::v-deep .vb-input {
    padding-right: $grid-gutter-20;

    &::placeholder {
      font-size: $font-size-base;
      color: $gray-500;
    }
  }

  ::v-deep .vb-select.form-richiesta-assistenza__select--empty {
    color: $gray-500;

    option {
      color: $gray-900;
    }
  }

  &__textarea {
    height: 130px;
    padding: $grid-gutter-20 / 2 $grid-gutter-20;
    font-size: $font-size-base;
    color: $gray-900;
    border-radius: $input-border-radius;

    &::placeholder {
      color: $gray-500;
    }
  }

  &__api-error {
    margin: 0;
    font-size: $font-size-md;
    color: $danger;
  }

  &__footer {
    display: flex;
    flex-wrap: wrap;
    gap: $grid-gutter-20;
    align-items: center;
    justify-content: space-between;
    margin-top: $grid-gutter-20;
  }

  &__consent {
    font-size: $font-size-md;
    color: $gray-900;
  }

  &__submit {
    min-width: 100px;
  }
}
</style>
